import type { NewBlockPreview } from '@/features/editor/lib/newBlockPreview'
import { THUMB_BOX_CLASS } from './layout'
import { renderKeyOf } from '@/features/editor/lib/blockRenderKeys'
import { useLeavingBlocks } from './useLeavingBlocks'
import { LeavingThumbnail } from './LeavingThumbnail'
import { cn } from '@teach/ui'
import { BrandLoader } from '@/components/common/BrandLoader'
import { type RefObject } from 'react'
import { Plus } from 'lucide-react'
import type { Block } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { AddBlockMenu } from '@/features/editor/components/blocks/AddBlockMenu'
import type { ActiveBlockCard } from '@/features/editor/constants/blockTypeMatrix'
import type { LessonType } from '@/types/lesson'
import { BlockThumbnail } from './BlockThumbnail'
import { CompactAddButton, CompactBlockChip } from './CompactBlockChip'
import { SortableItem } from './SortableItem'
import { SortableStrip } from './SortableStrip'
import { isBlockLocked, lockReasonOf, type BlocksInRun } from './useBlocksInRun'
import { lockHint } from './lockHint'
import { useMessages } from '@/i18n'
import { messages as blockMessages } from '../blocks/messages'

interface Props {
  blocks: Block[]
  isLoading: boolean
  isCreating: boolean
  selectedBlockId: number | null
  selectedRef: RefObject<HTMLDivElement>
  contentLang: ContentLang
  isCourseAuthor: boolean
  /** Блоки, занятые прогоном, пишущим в блоки (`useBlocksInRun`). */
  blocksInRun: BlocksInRun
  /** Курс на проверке: новый блок меняет контент заявки — «Добавить» и
   *  «Копия» гаснут. У ПРОГОНА «Копия» живая намеренно: она ничего не затирает. */
  isFrozen: boolean
  /** Студия переезжает на только что заведённый черновик (`useIsDraftSourceSettling`):
   *  перетаскивание гаснет. Опционален ради старых тестов этого компонента. */
  isSourceSettling?: boolean
  popoverOpen: boolean
  onPopoverChange: (open: boolean) => void
  onSelectBlock: (blockId: number) => void
  onDeleteBlock: (blockId: number) => void
  /** п.10: набор мультивыбора (Ctrl/Cmd-клик) — опционален ради старых тестов
   *  этого компонента, как `isBlockWritingRunActive` у `LessonExpanded`. */
  selectedBlockIds?: number[]
  onToggleSelectBlock?: (blockId: number) => void
  onAdd: (card: ActiveBlockCard) => void
  /** Копия экрана по id — встаёт сразу за ним. */
  onDuplicate: (blockId: number) => void
  onReorder: (fromIndex: number, toIndex: number) => void
  lessonType: LessonType
  /** Вид нового блока по типу — для перетекания в окне выбора (`AddBlockPicker`). */
  previewFor?: (card: ActiveBlockCard) => NewBlockPreview | null
  /** Свёрнутая лента (`useTimelineCollapsed`): плашки «номер + тип» вместо миниатюр. */
  compact?: boolean
}

/** Точка «занят прогоном» на плашке экрана — только у прогонов AI. Замок проверки
 *  курса пометок на экранах не даёт (решение заказчика: о проверке говорят окно
 *  публикации и погашенные кнопки), иначе каждая плашка горела бы точкой. */
const showsRunMark = (state: BlocksInRun, blockId: number): boolean =>
  isBlockLocked(state, blockId) && lockReasonOf(state) !== 'review'

export const BlockStrip = ({
  blocks,
  isLoading,
  isCreating,
  selectedBlockId,
  selectedRef,
  contentLang,
  isCourseAuthor,
  blocksInRun,
  isFrozen,
  isSourceSettling = false,
  popoverOpen,
  onPopoverChange,
  onSelectBlock,
  onDeleteBlock,
  onAdd,
  onDuplicate,
  onReorder,
  lessonType,
  previewFor,
  selectedBlockIds = [],
  onToggleSelectBlock = () => {},
  compact = false,
}: Props) => {
  const mb = useMessages(blockMessages)
  const items = useLeavingBlocks(blocks)
  return (
    <div
      className={cn(
        // `overflow-y-hidden` делает ось X прокручиваемой сама (`visible` → `auto`) —
        // без скрытия полоса прокрутки рисовалась линией под миниатюрами.
        'flex gap-2 overflow-y-hidden px-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        compact ? 'items-center py-1.5' : 'items-stretch pb-2 pt-8',
      )}
    >
      {isLoading && compact ? (
        <div className="h-8 w-24 shrink-0 animate-pulse rounded-full bg-muted" />
      ) : isLoading ? (
        // Скелет — во всю высоту плитки «+» (карточка ВМЕСТЕ с язычком, 20px над ней).
        <div className={cn('relative shrink-0', THUMB_BOX_CLASS)}>
          <div className="absolute inset-x-0 bottom-0 -top-5 animate-pulse rounded-lg bg-muted" />
        </div>
      ) : (
        <SortableStrip items={blocks} onReorder={onReorder}>
          {items.map((item, idx) => {
            const b = item.block
            // Удалённый блок ещё ~0.3 с схлопывается на своём месте (`useLeavingBlocks`).
            if (item.leaving && compact) return null
            if (item.leaving)
              return (
                <LeavingThumbnail
                  key={`leaving-${b.id}`}
                  block={b}
                  index={item.index}
                  contentLang={contentLang}
                />
              )
            return (
              <SortableItem
                key={renderKeyOf(b.id)}
                id={b.id}
                // C-1: реордер собирает ПОЛНЫЕ тела блоков из до-прогонного кеша
                // (`PUT` блока — полная замена, §3.2), поэтому перетаскивание
                // блока, который прямо сейчас переписывает прогон, затирает уже
                // оплаченный результат. Диапазон, задевающий такой блок чужим
                // перетаскиванием, отсекает `useLessonBlocksActions.handleReorder`.
                // Пока студия переезжает на черновик, тела реордера собраны из живой версии.
                disabled={!isCourseAuthor || isSourceSettling || isBlockLocked(blocksInRun, b.id)}
                // Только что созданный блок (ключ временной записи < 0) выезжает из-под
                // соседа; настоящий блок наследует ключ (`blockRenderKeys`), поэтому
                // замена не пересоздаёт узел и не обрывает анимацию.
                className={cn(
                  compact ? 'shrink-0' : 'h-full shrink-0',
                  renderKeyOf(b.id) < 0 && 'animate-thumb-in',
                )}
              >
                {compact ? (
                  <CompactBlockChip
                    block={b}
                    index={idx}
                    selected={popoverOpen ? false : b.id === selectedBlockId}
                    innerRef={b.id === selectedBlockId ? selectedRef : undefined}
                    multiSelected={selectedBlockIds.includes(b.id)}
                    inRun={showsRunMark(blocksInRun, b.id)}
                    onSelect={() => {
                      onPopoverChange(false)
                      onSelectBlock(b.id)
                    }}
                    onToggleSelect={() => onToggleSelectBlock(b.id)}
                  />
                ) : (
                  <BlockThumbnail
                    block={b}
                    index={idx}
                    selected={popoverOpen ? false : b.id === selectedBlockId}
                    innerRef={b.id === selectedBlockId ? selectedRef : undefined}
                    onSelect={() => {
                      // Клик по экрану — выход из выбора типа нового экрана.
                      onPopoverChange(false)
                      onSelectBlock(b.id)
                    }}
                    onDelete={() => onDeleteBlock(b.id)}
                    onDuplicate={() => onDuplicate(b.id)}
                    duplicateDisabled={isCreating || !isCourseAuthor || isFrozen}
                    duplicateTitle={lockHint(isCourseAuthor, isFrozen)}
                    onToggleSelect={() => onToggleSelectBlock(b.id)}
                    multiSelected={selectedBlockIds.includes(b.id)}
                    isCourseAuthor={isCourseAuthor}
                    contentLang={contentLang}
                    inRun={isBlockLocked(blocksInRun, b.id)}
                    lockReason={lockReasonOf(blocksInRun)}
                  />
                )}
              </SortableItem>
            )
          })}
        </SortableStrip>
      )}
      {/* Копия уже видна в ленте временной плашкой — второй заглушки в конце не нужно. */}
      {isCreating && !blocks.some((b) => b.id < 0) && compact ? (
        <div
          data-testid="block-creating-skeleton"
          className="h-8 w-12 shrink-0 animate-pulse rounded-full bg-muted"
        />
      ) : isCreating && !blocks.some((b) => b.id < 0) ? (
        // Как плитка «+»: во всю высоту карточки вместе с язычком (20px над ней).
        <div className={cn('relative shrink-0', THUMB_BOX_CLASS)}>
          <div
            data-testid="block-creating-skeleton"
            className="absolute inset-x-0 bottom-0 -top-5 flex animate-pulse items-center justify-center rounded-lg border border-dashed bg-muted/50"
          >
            <BrandLoader className="size-6" />
          </div>
        </div>
      ) : null}
      {compact ? (
        <CompactAddButton
          onClick={() => onPopoverChange(true)}
          disabled={isCreating || !isCourseAuthor || isFrozen}
          pressed={popoverOpen}
          title={lockHint(isCourseAuthor, isFrozen)}
        />
      ) : (
        /* Плитка «+» — во всю высоту карточки ВМЕСТЕ с язычком (20px над ней). */
        <div className={cn('relative shrink-0', THUMB_BOX_CLASS)}>
          <button
            type="button"
            aria-label={mb.addBlock}
            onClick={() => onPopoverChange(true)}
            disabled={isCreating || !isCourseAuthor || isFrozen}
            title={lockHint(isCourseAuthor, isFrozen)}
            aria-pressed={popoverOpen}
            className={cn(
              'absolute inset-x-0 bottom-0 -top-5 flex items-center justify-center rounded-lg border transition disabled:cursor-not-allowed disabled:opacity-50',
              // На время создания блока плитка выключена, но НЕ бледнеет и не
              // подсвечивается наведением: иначе она мигала на каждом добавлении.
              isCreating && 'pointer-events-none disabled:opacity-100',
              // Пока открыт выбор типа, плитка «+» и есть выделенный экран — тот, что создаём.
              popoverOpen
                ? 'border-2 border-solid border-primary bg-primary/15 text-primary'
                : 'border-dashed border-muted-foreground/40 bg-background text-muted-foreground hover:border-primary/50 hover:bg-primary/5 hover:text-primary',
            )}
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
      )}
      {popoverOpen ? (
        <AddBlockMenu
          onAdd={onAdd}
          previewFor={previewFor}
          onClose={() => onPopoverChange(false)}
          lessonType={lessonType}
        />
      ) : null}
    </div>
  )
}
